import React, { useContext, useState, useMemo, useEffect } from 'react';
import { inputStyle, labelStyle, sectionedPanelStyle } from '../../../styles';
import { AsButton, AsDatePicker, AsSelect, Spacer, AsDropdown } from '@adserve/adserve-react-components';
import moment from 'moment';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';
import { PerformanceContext } from '../../../contexts/PerformanceContext';
import MultiSelection from './MultiSelect';
import { ReactComponent as IconReset } from '../../../svg/icon_reset.svg';

const PerformanceCoverage = ({
    campaign,
    id = 'PerformanceCoverage',
    performanceStats = {},
    properties,
    airtimeBursts,
    filters,
    setFilters,
    demographics = [],
    stations = [],
    surveys = [],
    dayParts = [],
    scriptFactors = [],
    versions = [],
    spotTypes,
    burstOrPropertyChange
}) => {
    const minDate = useMemo(() => moment(campaign?.startDate).startOf('week'), [campaign]);
    const maxDate = useMemo(() => moment(campaign?.endDate).endOf('week'), [campaign]);
    const [burstProperties, setBurstProperties] = useState([]);
    const [selectedBurstProperty, setSelectedBurstProperty] = useState('');
    const { dateFormat } = useContext(SystemSettingsContext);
    const { resetData } = useContext(PerformanceContext); // ASSP-7730

    const textStyle = {
        fontFamily: 'Roboto',
        fontSize: 17,
        color: '#414141',
        textDecoration: 'none solid rgb(65, 65, 65)',
        fontWeight: 'bold'
    };

    const handleReset = () => { // ASSP-7730
        resetData();
    };
    const handleSurveyChange = (event) => {
        filters.surveyId = event?.target?.value;
        setFilters({ ...filters });
    };
    const handleVersionChange = (event) => {
        filters.versionId = event?.target?.value;
        setFilters({ ...filters });
    };
    const handleSpotTypeChange = (types) => {
        if (types.filter((t) => t.id == 0).length) {
            filters.spotTypeIds = [];
        } else {
            filters.spotTypeIds = types.filter((t) => t.id).map((a) => a.id);
        }
        setFilters({ ...filters });
    };
    const handleDaypartsChange = (types) => {
        if (types.filter((t) => t.id == 0).length) {
            filters.dayPartIds = [];
        } else {
            filters.dayPartIds = types.filter((t) => t.id).map((a) => a.id);
        }
        setFilters({ ...filters });
    };
    const handleScriptFactorsChange = (types) => {
        if (types.filter((t) => t.id == 0).length) {
            filters.scriptFactorIds = [];
        } else {
            filters.scriptFactorIds = types.filter((t) => t.id).map((a) => a.id);
        }
        setFilters({ ...filters });
    };
    const handleStationsChange = (types) => {
        if (types.filter((t) => t.id == 0).length) {
            filters.stationIds = [];
        } else {
            filters.stationIds = types.filter((t) => t.id).map((a) => a.id);
        }
        setFilters({ ...filters });
    };
    const handleDemographicChange = (event) => {
        filters.demographicId = event?.target?.value;
        setFilters({ ...filters });
    };

    const handleStartDateChange = (event) => {
        const value = moment(event.target.value).utc(true).startOf('day');
        filters.dateRange[0] = value.toISOString();
        if (moment(filters.dateRange[0]) > moment(filters.dateRange[1])) {
            filters.dateRange[1] = moment(filters.dateRange[0]).add('days', 6).toISOString();
        }
        setFilters({ ...filters });
    };

    const handleEndDateChange = (event) => {
        const value = moment(event.target.value).utc(true).startOf('day');
        filters.dateRange[1] = value.toISOString();
        if (moment(filters.dateRange[0]) > moment(filters.dateRange[1])) {
            filters.dateRange[0] = moment(filters.dateRange[1]).add('days', -6).toISOString();
        }
        setFilters({ ...filters });
    };
    useEffect(() => {
        setBurstProperties([
            { id: 0, type: 0, name: window.translate('- Please Select -') },
            ...properties.map((p) => {
                return { id: p.id, name: p.name, type: 1 };
            }),
            ...airtimeBursts.map((p) => {
                return { id: p.id, name: p.name, type: 2 };
            })
        ]);
    }, [airtimeBursts, properties]);

    const handlePropertyBurstsChange = (types) => {
        if (types.filter((t) => t.id == 0).length) {
            filters.campaignBurstIds = [];
            filters.propertySelectionIds = [];
        } else {
            filters.campaignBurstIds = types.filter((t) => t.type === 2).map((t) => t.id);
            filters.propertySelectionIds = types.filter((t) => t.type === 1).map((t) => t.id);
        }
        setFilters({ ...filters });
    };

    // ASSP-7703
    // Reduced padding to 10px - need extra space for ASSP-7730 reset button
    // Removed unnecessary display 'flex' from blocking Properties/Bursts width
    // Aligned datepickers by separating them and setting font to 10
    // Reduced fontSize and added bottom margin for survey, audience, version
    return (
        <div
            id={id}
            style={{
                ...sectionedPanelStyle,
                display: 'grid',
                padding: '0 10px',
                height: 78,
                gap: '5px 0px',
                boxSizing: 'border-box',
                width: '100%',
                alignItems: 'center',
                // gridTemplateColumns: '2fr 3fr 6fr 4fr 1fr',
                gridTemplateColumns: properties?.length
                    ? '1.15fr 1fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.3fr 0.1fr'
                    : '0.55fr 0.7fr 1fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.3fr 0.1fr',
                gridColumn: '1 / -1'
            }}
        >
            {burstProperties?.length > 1 ? (
                <>
                    <div>
                        <MultiSelection
                            values={burstProperties}
                            initialValues={filters.propertySelectionIds} // ASSP-7730
                            onApply={handlePropertyBurstsChange}
                            inputStyle={
                                burstProperties.length <= 1
                                    ? { marginTop: 4, height: 40, width: 'auto', backgroundColor: 'rgb(247,247,247)' }
                                    : { marginTop: 4, height: 40, width: 'auto' }
                            }
                            indexKey='propertyBursts'
                            title={window.translate('Properties/Bursts')}
                            width='300'
                            disabled={burstProperties.length <= 1}
                            applyRequired={true}
                            labelStyle={{ fontSize: 10 }}
                        ></MultiSelection>
                    </div>
                </>
            ) : (
                <>
                    <div style={{ display: 'flex', width: '100%' }}>
                        <span style={textStyle}>{window.translate('Completion Rate:')}</span>
                    </div>
                    <div style={{ display: 'flex', width: '100%', padding: '0 4px' }}>
                        <span style={textStyle}>{'0%'}</span>
                        <Spacer width={11} height={0} />
                        <div style={{ width: 120, marginTop: 3 }}>
                            <div style={{ backgroundColor: '#DADADA', borderRadius: 10, height: 13, width: '100%' }} />
                            <div
                                style={{
                                    position: 'absolute',
                                    backgroundColor: '#FF7D00',
                                    borderRadius: 10,
                                    height: 13,
                                    marginTop: -13,
                                    width:
                                        235 *
                                        ((performanceStats?.completionRate ? performanceStats?.completionRate : 0.0) /
                                            100)
                                }}
                            />
                        </div>
                        <Spacer width={8} height={0} />
                        <span style={textStyle}>{'100%'}</span>
                    </div>
                </>
            )}

            <div style={{ padding: '0 4px' }}>
                <MultiSelection
                    values={stations}
                    initialValues={filters.stationIds} // ASSP-7730
                    onApply={handleStationsChange}
                    inputStyle={
                        stations.length <= 1
                            ? { marginTop: 4, height: 40, width: 'auto', backgroundColor: 'rgb(247,247,247)' }
                            : { marginTop: 4, height: 40, width: 'auto' }
                    }
                    indexKey='stations'
                    title={window.translate('Stations')}
                    width='329'
                    disabled={stations.length <= 1}
                    applyRequired={true}
                    labelStyle={{ fontSize: 10 }}
                ></MultiSelection>
            </div>

            <div style={{ paddingRight: 4 }}>
                <label style={{ ...labelStyle, fontSize: 10 }}>{window.translate('From')}</label>
                <AsDatePicker
                    id='order-start-date'
                    name='startDate'
                    labelStyle={{ ...labelStyle, marginBottom: 0 }}
                    inputStyle={inputStyle}
                    currentDate={filters.dateRange[0]}
                    minDate={minDate}
                    maxDate={maxDate}
                    placeholder=''
                    onChange={handleStartDateChange}
                    // disabled={!dayParts.length} // ASSP-8141 Why? Go to range with no data, can't reset
                    dateFormat={dateFormat}
                    containerStyle={{ width: '100%' }}
                    style={{ flexWrap: 'no-wrap' }}
                    buttonStyle={{ width: 56 }}
                />
            </div>

            <div style={{ paddingRight: 4 }}>
                <label style={{ ...labelStyle, fontSize: 10 }}>{window.translate('To')}</label>
                <AsDatePicker
                    id='order-end-date'
                    name='startDate'
                    labelStyle={{ ...labelStyle, marginBottom: 0 }}
                    inputStyle={inputStyle}
                    currentDate={filters.dateRange[1]}
                    minDate={minDate}
                    maxDate={maxDate}
                    placeholder=''
                    onChange={handleEndDateChange}
                    // disabled={!dayParts.length} // ASSP-8141 Why? Go to range with no data, can't reset
                    dateFormat={dateFormat}
                    containerStyle={{ width: '100%' }}
                    style={{ flexWrap: 'no-wrap' }}
                    buttonStyle={{ width: 56 }}
                />
            </div>

            <div style={{ padding: '0 4px' }}>
                <MultiSelection
                    values={dayParts}
                    initialValues={filters.dayPartIds} // ASSP-7730
                    onApply={handleDaypartsChange}
                    inputStyle={
                        dayParts.length <= 1
                            ? { marginTop: 4, height: 40, width: 'auto', backgroundColor: 'rgb(247,247,247)' }
                            : { marginTop: 4, height: 40, width: 'auto' }
                    }
                    indexKey='dayParts'
                    title={window.translate('Day Parts')}
                    width='329'
                    disabled={dayParts.length <= 1}
                    applyRequired={true}
                    labelStyle={{ fontSize: 10 }}
                ></MultiSelection>
            </div>

            <div style={{ padding: '0 4px' }}>
                <MultiSelection
                    values={scriptFactors}
                    initialValues={filters.scriptFactorIds} // ASSP-7730
                    onApply={handleScriptFactorsChange}
                    inputStyle={
                        scriptFactors.length <= 1
                            ? { marginTop: 4, height: 40, width: 'auto', backgroundColor: 'rgb(247,247,247)' }
                            : { marginTop: 4, height: 40, width: 'auto' }
                    }
                    indexKey='scriptFactors'
                    title={window.translate('Script Factors')}
                    width='329'
                    disabled={scriptFactors.length <= 1}
                    applyRequired={true}
                    labelStyle={{ fontSize: 10 }}
                ></MultiSelection>
            </div>
            <div style={{ padding: '0 4px' }}>
                <MultiSelection
                    values={spotTypes}
                    initialValues={filters.spotTypeIds} // ASSP-7730
                    onApply={handleSpotTypeChange}
                    inputStyle={
                        spotTypes.length <= 1
                            ? { marginTop: 4, height: 40, width: 'auto', backgroundColor: 'rgb(247,247,247)' }
                            : { marginTop: 4, height: 40, width: 'auto' }
                    }
                    onChange={handleSpotTypeChange}
                    indexKey='spotTypes'
                    title={window.translate('Spot Types')}
                    width='329'
                    disabled={spotTypes.length <= 1}
                    applyRequired={true}
                    labelStyle={{ fontSize: 10 }}
                ></MultiSelection>
            </div>

            <div style={{ padding: '0 4px' }}>
                <AsSelect
                    id='surveys'
                    name='surveyId'
                    selectStyle={{ ...inputStyle, fontSize: 12 }}
                    values={surveys}
                    value={filters.surveyId}
                    onChange={handleSurveyChange}
                    disabled={surveys.length <= 1}
                    disableTooltip={true}
                    title={window.translate('Survey')}
                    labelStyle={{ fontSize: 10, marginBottom: 4 }}
                />
            </div>

            <div style={{ padding: '0 4px' }}>
                <AsSelect
                    id='demographics'
                    name='demographicId'
                    selectStyle={{ ...inputStyle, fontSize: 12 }}
                    values={demographics}
                    value={filters.demographicId}
                    onChange={handleDemographicChange}
                    disabled={demographics.length <= 1}
                    disableTooltip={true}
                    title={window.translate('Audience')}
                    labelStyle={{ fontSize: 10, marginBottom: 4 }}
                />
            </div>

            <div style={{ padding: '0 4px' }}>
                <AsSelect
                    id='versions'
                    name='versionId'
                    selectStyle={{ ...inputStyle, fontSize: 12 }}
                    values={versions}
                    value={filters.versionId}
                    onChange={handleVersionChange}
                    disabled={versions.length <= 1}
                    disableTooltip={true}
                    title={window.translate('Version')}
                    labelStyle={{ fontSize: 10, marginBottom: 4 }}
                />
            </div>

            <div style={{ padding: '17px 4px 0' }}> {/* ASSP-7730 */}
                <AsButton 
                    style={{width: 41, height: 40 }}
                    handleClick={handleReset}
                >
                    <div style={{ marginTop: 7 }}>
                        <IconReset
                            style={{ 
                                id: 'reset',
                                width: 21,
                                height: 21,
                            }}
                        />
                    </div>
                </AsButton>
            </div>
        </div>
    );
};

export default PerformanceCoverage;
